/**
 * Catch-all shim for the residue of Fluent UI imports left after the
 * component swap: style helpers (`makeStyles`, `mergeStyles`, `shorthands`),
 * theme glue (`ThemeProvider`, `PartialTheme`), init helpers
 * (`registerIcons`, `initializeIcons`, `nullRender`), enums (`Position`,
 * `SelectionMode`, `SelectableOptionMenuItemType`, `DropdownMenuItemType`,
 * `Selection`), miscellaneous icons (`EndPointSolidIcon`,
 * `SettingsCogMultipleRegular`), and assorted type aliases.
 *
 * The whole point of this file is to let us `npm uninstall @fluentui/*`
 * without rewriting every call site. Anything that resists clean translation
 * gets a no-op stub.
 */

/* eslint-disable @typescript-eslint/no-redeclare */
import * as React from "react";
import { Activity, Settings } from "lucide-react";

// ─── makeStyles / mergeStyles runtime ─────────────────────────────────────────
//
// Both are tiny CSS-in-JS APIs. We implement them with a shared minimal
// runtime that turns flat style objects into class names by injecting CSS
// rules into a single `<style id="fluent-compat-styles">` tag.

let _styleSheet: HTMLStyleElement | null = null;
const _injected = new Set<string>();

function getSheet(): HTMLStyleElement | null {
  if (typeof document === "undefined") return null;
  if (_styleSheet) return _styleSheet;
  _styleSheet = document.createElement("style");
  _styleSheet.id = "fluent-compat-styles";
  document.head.appendChild(_styleSheet);
  return _styleSheet;
}

const UNITLESS = new Set([
  "opacity",
  "zIndex",
  "fontWeight",
  "lineHeight",
  "flex",
  "flexGrow",
  "flexShrink",
  "order",
  "columnCount",
]);

const camelToKebab = (k: string): string =>
  k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

function styleObjToCss(obj: Record<string, any>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v == null || v === false) continue;
    // Nested selectors etc. get flattened to a CSS comment and dropped — they
    // would require a real CSS-in-JS engine to express.
    if (typeof v === "object") continue;
    const value = typeof v === "number" && !UNITLESS.has(k) ? `${v}px` : v;
    parts.push(`${camelToKebab(k)}: ${value};`);
  }
  return parts.join(" ");
}

function hashStr(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
}

function injectRule(css: string): string {
  if (!css) return "";
  const className = `tri-css-${hashStr(css)}`;
  if (_injected.has(className)) return className;
  const sheet = getSheet();
  if (sheet) {
    sheet.appendChild(document.createTextNode(`.${className} { ${css} }\n`));
    _injected.add(className);
  }
  return className;
}

/**
 * `makeStyles({ key: { ...css } })` — returns a hook that yields
 * `{ key: 'generated-class' }`. Class names are stable across renders, so
 * call sites doing `styles.foo` always get the same string.
 */
export function makeStyles<T extends Record<string, Record<string, any>>>(
  definitions: T,
): () => Record<keyof T, string> {
  const result: Record<string, string> = {};
  for (const key in definitions) {
    result[key] = injectRule(styleObjToCss(definitions[key]));
  }
  return () => result as Record<keyof T, string>;
}

/**
 * `mergeStyles(...)` — accepts style objects and class-name strings, returns
 * a single space-joined class-name string.
 */
export function mergeStyles(
  ...styles: Array<Record<string, any> | string | false | null | undefined>
): string {
  const classes: string[] = [];
  for (const s of styles) {
    if (!s) continue;
    if (typeof s === "string") {
      classes.push(s);
    } else if (typeof s === "object") {
      const css = styleObjToCss(s);
      const cls = injectRule(css);
      if (cls) classes.push(cls);
    }
  }
  return classes.join(" ");
}

/**
 * `mergeStyleSets({ key: { ...css } })` — same shape as makeStyles output but
 * returns synchronously instead of being a hook.
 */
export function mergeStyleSets<
  T extends Record<string, Record<string, any>>,
>(definitions: T): Record<keyof T, string> {
  const result: Record<string, string> = {};
  for (const key in definitions) {
    result[key] = injectRule(styleObjToCss(definitions[key]));
  }
  return result as Record<keyof T, string>;
}

// ─── shorthands ───────────────────────────────────────────────────────────────
//
// Fluent v9's CSS shorthand expansion. We use it spread inline:
// `{ ...shorthands.padding('10px') }`. Each helper returns a flat object the
// makeStyles runtime understands.

type ShorthandValue = string | number;

const sides = ["Top", "Right", "Bottom", "Left"] as const;

function expandSides(prefix: string, args: ShorthandValue[]): Record<string, ShorthandValue> {
  // Mirrors the CSS shorthand: 1 arg → all sides, 2 → vert/horiz, 3 → top/h/bottom, 4 → t/r/b/l.
  const [a, b, c, d] = args;
  const v = (() => {
    switch (args.length) {
      case 1: return [a, a, a, a];
      case 2: return [a, b, a, b];
      case 3: return [a, b, c, b];
      default: return [a, b, c, d];
    }
  })();
  const out: Record<string, ShorthandValue> = {};
  sides.forEach((side, i) => {
    if (v[i] !== undefined) out[`${prefix}${side}`] = v[i] as ShorthandValue;
  });
  return out;
}

function borderShorthand(...args: any[]): Record<string, any> {
  return { border: args.join(" ") };
}

export const shorthands = {
  padding: (...args: ShorthandValue[]) => expandSides("padding", args),
  margin: (...args: ShorthandValue[]) => expandSides("margin", args),
  inset: (...args: ShorthandValue[]) => {
    // Inset in CSS is top/right/bottom/left; mirror.
    const [a, b, c, d] = args;
    const v =
      args.length === 1
        ? [a, a, a, a]
        : args.length === 2
          ? [a, b, a, b]
          : args.length === 3
            ? [a, b, c, b]
            : [a, b, c, d];
    return { top: v[0], right: v[1], bottom: v[2], left: v[3] };
  },
  border: (...args: any[]) => borderShorthand(...args),
  borderTop: (...args: any[]) => ({ borderTop: args.join(" ") }),
  borderRight: (...args: any[]) => ({ borderRight: args.join(" ") }),
  borderBottom: (...args: any[]) => ({ borderBottom: args.join(" ") }),
  borderLeft: (...args: any[]) => ({ borderLeft: args.join(" ") }),
  borderColor: (...args: any[]) => ({ borderColor: args.join(" ") }),
  borderStyle: (...args: any[]) => ({ borderStyle: args.join(" ") }),
  borderWidth: (...args: any[]) => ({ borderWidth: args.join(" ") }),
  borderRadius: (...args: ShorthandValue[]) => ({
    borderRadius: args.join(" "),
  }),
  gap: (...args: ShorthandValue[]) => ({ gap: args.join(" ") }),
  overflow: (...args: any[]) => ({ overflow: args.join(" ") }),
  textDecoration: (...args: any[]) => ({ textDecoration: args.join(" ") }),
  flex: (...args: any[]) => ({ flex: args.join(" ") }),
  outline: (...args: any[]) => ({ outline: args.join(" ") }),
};

// ─── ThemeProvider ────────────────────────────────────────────────────────────
//
// shadcn already drives theming via `--*` CSS variables on `<html>`. The
// Fluent ThemeProvider becomes a transparent passthrough so existing
// `<ThemeProvider theme={…}>` wrappers don't need to be deleted by hand.

export interface PartialTheme {
  palette?: any;
  fonts?: any;
  semanticColors?: any;
  [key: string]: any;
}

export interface FluentThemeProviderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  theme?: PartialTheme | any;
  applyTo?: string;
  as?: React.ElementType;
}

export const ThemeProvider: React.FC<FluentThemeProviderProps> = ({
  theme: _theme,
  applyTo: _applyTo,
  as,
  children,
  ...rest
}) => {
  const Comp = (as ?? "div") as React.ElementType;
  return <Comp {...rest}>{children}</Comp>;
};
ThemeProvider.displayName = "ThemeProvider";

// ─── Init / no-op stubs ───────────────────────────────────────────────────────

/** Fluent v8's icon-font initializer — no-op (we use lucide). */
export function initializeIcons(_baseUrl?: string, _options?: any): void {
  /* no-op */
}

/** Fluent's runtime icon registry — no-op. */
export function registerIcons(_registration: any, _options?: any): void {
  /* no-op */
}

/** Fluent's "render nothing" sentinel. */
export function nullRender(): null {
  return null;
}

/** Bag of class names exported by Fluent's data grid; we don't render one. */
export const dataGridSelectionCellClassNames = {} as Record<string, string>;

// ─── Enums ────────────────────────────────────────────────────────────────────

/** Fluent's `Position` numeric enum (top=0, end=1, bottom=2, start=3). */
export const Position = {
  top: 0,
  end: 1,
  bottom: 2,
  start: 3,
} as const;
export type Position = (typeof Position)[keyof typeof Position];

export const SelectionMode = {
  none: 0,
  single: 1,
  multiple: 2,
} as const;
export type SelectionMode = (typeof SelectionMode)[keyof typeof SelectionMode];

export const SelectableOptionMenuItemType = {
  Normal: 0,
  Divider: 1,
  Header: 2,
  SelectAll: 3,
} as const;
export type SelectableOptionMenuItemType =
  (typeof SelectableOptionMenuItemType)[keyof typeof SelectableOptionMenuItemType];

export const DropdownMenuItemType = {
  Normal: 0,
  Divider: 1,
  Header: 2,
} as const;
export type DropdownMenuItemType =
  (typeof DropdownMenuItemType)[keyof typeof DropdownMenuItemType];

// ─── Selection class (data-grid plumbing) ─────────────────────────────────────
//
// Fluent's `Selection` was a stateful object passed to `DetailsList`. We
// don't render one anymore — the class is a tiny stub that satisfies type
// checks at the call sites that still construct it.

type SelectionItem = { key?: string | number; [k: string]: any };
type SelectionOptions = {
  onSelectionChanged?: () => void;
};

export class Selection<T extends SelectionItem = SelectionItem> {
  private _items: T[] = [];
  private _selected = new Set<string>();
  private _onSelectionChanged?: () => void;

  constructor(options?: SelectionOptions) {
    this._onSelectionChanged = options?.onSelectionChanged;
  }

  private _notifySelectionChanged(): void {
    this._onSelectionChanged?.();
  }

  setItems(items: T[]): void {
    this._items = items;
    this._notifySelectionChanged();
  }
  getItems(): T[] {
    return this._items;
  }
  getSelection(): T[] {
    return this._items.filter((it) =>
      this._selected.has(String((it as any).key ?? "")),
    );
  }
  getSelectedCount(): number {
    return this._selected.size;
  }
  setKeySelected(key: string | number, isSelected: boolean): void {
    const k = String(key);
    if (isSelected) this._selected.add(k);
    else this._selected.delete(k);
    this._notifySelectionChanged();
  }
  isKeySelected(key: string | number): boolean {
    return this._selected.has(String(key));
  }
  setAllSelected(isSelected: boolean): void {
    this._selected.clear();
    if (isSelected) {
      for (const it of this._items) {
        const k = String((it as any).key ?? "");
        if (k) this._selected.add(k);
      }
    }
    this._notifySelectionChanged();
  }
  toggleAllSelected(): void {
    if (this._selected.size === this._items.length) this.setAllSelected(false);
    else this.setAllSelected(true);
  }
  setIndexSelected(_index: number, _isSelected: boolean): void {
    /* index-based selection unused; no-op */
  }
}

// ─── Icons that came from the Fluent icon packages ────────────────────────────

export const EndPointSolidIcon: React.FC<React.SVGAttributes<SVGElement>> = (
  props,
) => <Activity {...(props as any)} />;
EndPointSolidIcon.displayName = "EndPointSolidIcon";

export const SettingsCogMultipleRegular: React.FC<
  React.SVGAttributes<SVGElement>
> = (props) => <Settings {...(props as any)} />;
SettingsCogMultipleRegular.displayName = "SettingsCogMultipleRegular";

// ─── Type-only aliases ────────────────────────────────────────────────────────

export type IButtonStyles = any;
export type ITextStyles = any;
export type ITextFieldStyles = any;
export type ITextField = any;
export type ITextFieldProps = any;
export type ITooltipProps = any;
export type IPanelProps = any;
export type IRenderFunction<P = any> = (
  props?: P,
  defaultRender?: (props?: P) => React.ReactNode,
) => React.ReactNode;
